/**
 * WOHER DER PUNKT KOMMT, AN DEM DIESE KARTE HÄNGT - AN BEIDEN KARTEN.
 *
 * ORIGINALTEXT MARVIN (08.09.2026, zum Bildschirmfoto „Vulkanstr. 37,
 * 56645 Nickenich")
 * > „er findet die adresse wieder nicht, wenn er die straße und hausnummer
 * >  nicht finden kann, was mich wundert, dann soll er zumindest die straße
 * >  gefunden werden, damit man in der nähe ist. aber sowas mit fehler ist die
 * >  schlechteste option!"
 *
 * **DIESER KASTEN IST DER GRUND, WARUM DER STRASSENTREFFER ÜBERHAUPT
 * GESPEICHERT WERDEN DARF.** Bis zum 08.09.2026 wurde er verworfen, und die
 * Begründung stand seit dem 25.08.2026 in `regeln/geokodierung.ts`: „EIN
 * GERATENER PUNKT IST SCHLIMMER ALS KEINER. Ein fehlender Punkt fällt auf: die
 * Karte sagt es. Ein Punkt in der Ortsmitte fällt NICHT auf, und dann fährt
 * der Bagger dorthin."
 *
 * Der Satz bleibt wahr. Zurückgenommen ist nur seine Folgerung, und zwar genau
 * so weit, wie dieser Kasten trägt: **ein Punkt, den man ALS UNGENAU ERKENNT,
 * ist kein geratener.** Fehlt er, ist die Lockerung von Migration 186 keine
 * Lockerung mehr, sondern ein Bruch derselben Regel.
 *
 * Er steht deshalb hier, ohne Seite darum, und beide Bereiche zeigen ihn -
 * dieselbe Aufteilung wie bei `KeinePunkte.tsx`. Zwei Fassungen liefen beim
 * ersten Nachtrag auseinander, und dann sagt der Lageplan etwas anderes über
 * denselben Punkt als die Planung daneben.
 *
 * ----------------------------------------------------------------------------
 * UND DER AUSWEG IST SEIT .161 EINE NACHBARNUMMER UND KEINE ZWEI ZAHLEN
 * ----------------------------------------------------------------------------
 *
 * ORIGINALTEXT MARVIN (10.09.2026)
 * > „die eingabe von koordinaten kannst du bei beiden karten komplett
 * >  entfernen. wir ersetzen die eingabe damit."
 *
 * Hier stand „Koordinaten von Hand eintragen" mit zwei Zahlenfeldern. Der
 * Ausweg ist derselbe geblieben und heisst jetzt: eine ANDERE Anschrift in der
 * Nähe suchen. **Und die Genauigkeit wird dabei nie besser als der Fund** -
 * bleibt es bei einem Straßentreffer, bleibt es bei `strasse`, und dieser
 * Kasten steht weiter da. Sonst verschwände er, und der Punkt wäre wieder ein
 * geratener.
 */
import Adresssuche, { type Anschrift } from './Adresssuche.tsx';

/**
 * Der Kasten über einer Karte, deren Punkt auf der Straße liegt.
 *
 * ER ERSCHEINT NUR BEI `strasse`. Ein Haustreffer und ein von Hand gesetzter
 * Punkt sind so genau, wie dieses System es kann; ein Kasten darüber wäre eine
 * Zeile, die immer dasteht - und die wird auch an dem Tag nicht gelesen, an
 * dem sie etwas sagt. **Und LEER heisst „wir wissen es nicht"** (eine Adresse
 * aus der Zeit vor Migration 186) und ausdrücklich nicht „ungenau": eine
 * Warnung über einem Punkt, der aus einer Hausnummer stammt, ist genauso
 * falsch wie keine über einem, der es nicht tut.
 */
export default function Punktgenauigkeit(
  { auftragId, genauigkeit, hinweis, anschrift, fertig }: {
    auftragId: string;
    genauigkeit?: string | null;
    hinweis?: string | null;
    anschrift?: Anschrift | null;
    fertig?: () => void;
  }
) {
  if (genauigkeit !== 'strasse') return null;

  return (
    <div className="hinweis warnung" style={{ marginBottom: 8 }}>
      <b>Dieser Punkt liegt auf der Straße, nicht am Haus</b>
      {/* DER SATZ KOMMT VOM SERVER UND WIRD HIER NICHT NACHGEBAUT. Er nennt
          die gefundene Straße beim Namen, wenn der Fund gerade vorlag; eine
          zweite Fassung daneben liefe beim ersten Nachtrag auseinander. */}
      <div className="klein">
        {hinweis
         || 'Der Suchdienst kennt diese Hausnummer nicht. Bei einer '
          + 'Ortsdurchfahrt sind das bis zu 300 Meter.'}
      </div>
      {/* UND ER NENNT DEN NÄCHSTEN SCHRITT. Ein Kasten, der einen Mangel
          benennt und keinen Weg daneben stellt, ist eine Liste von
          Vorwürfen - dieselbe Regel wie bei „Was noch fehlt" in der Akte. */}
      <div className="klein leise" style={{ marginTop: 8 }}>
        Für die Umgebungsbilder und den ersten Blick reicht der Punkt. Für den
        Lageplan an die Gemeinde setzen Sie die Montagegrube an die richtige
        Stelle - sie schlägt diesen Punkt.
      </div>
      <Adresssuche auftragId={auftragId} anschrift={anschrift}
                   aufschrift="Andere Anschrift suchen" fertig={fertig} />
    </div>
  );
}
